import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tooltip as RadixTooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Info } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { useIsMobile } from "@/hooks/use-mobile";
import { CategoryBreakdownData, CategoryBreakdownItem } from "./interfaces";
import { formatCurrency } from "./utils";

interface CategoryBreakdownChartsProps {
  data: CategoryBreakdownData[];
  loading?: boolean;
  rangeLabel: string;
}

const OTHERS_THRESHOLD = 5;

// Color palette for categories
const COLORS = [
  '#3b82f6', // blue
  '#10b981', // green
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // purple
  '#ec4899', // pink
  '#14b8a6', // teal
  '#f97316', // orange
  '#6366f1', // indigo
  '#06b6d4', // cyan
];

const OTHERS_COLOR = '#94a3b8'; // slate

type ChartEntry = CategoryBreakdownItem & { name: string; value: number };

const CustomTooltip = ({ active, payload }: { active?: boolean; payload?: Array<{ payload: ChartEntry }> }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const displayPercentage = data.percentage_of_total === 0 ? 1 : data.percentage_of_total;
    return (
      <div className="bg-background border border-border rounded-lg p-3 shadow-lg">
        <p className="font-semibold text-sm">{data.category_name}</p>
        <p className="text-sm text-muted-foreground">
          Amount: {formatCurrency(data.total_amount)}
        </p>
        {data.category_name !== 'Others' && (
          <p className="text-sm text-muted-foreground">
            Transactions: {data.transaction_count}
          </p>
        )}
        <p className="text-sm text-muted-foreground">
          {displayPercentage.toFixed(1)}% of total
        </p>
      </div>
    );
  }
  return null;
};

// Format month string to "MM/yyyy"
// Handles "MM-yyyy" (from API), "yyyy-MM", and "MM/yyyy" formats
const formatMonth = (monthKey: string): string => {
  if (!monthKey) return '';

  // Already in MM/yyyy format
  if (/^\d{1,2}\/\d{4}$/.test(monthKey)) return monthKey;

  // Try splitting by '-'
  const parts = monthKey.split('-');
  if (parts.length === 2) {
    let month: string;
    let year: string;
    if (parts[0].length === 4) {
      year = parts[0];
      month = parts[1].padStart(2, '0');
    } else {
      month = parts[0].padStart(2, '0');
      year = parts[1];
    }
    const monthNum = parseInt(month);
    if (monthNum >= 1 && monthNum <= 12) return `${month}/${year}`;
  }

  return monthKey;
};

const buildChartData = (categories: CategoryBreakdownItem[]): { data: ChartEntry[]; colors: string[]; othersDetail: CategoryBreakdownItem[] } => {
  const main = categories.filter(cat => cat.percentage_of_total >= OTHERS_THRESHOLD);
  const others = categories.filter(cat => cat.percentage_of_total < OTHERS_THRESHOLD)
    .sort((a, b) => b.percentage_of_total - a.percentage_of_total);
  const currency = categories[0]?.currency ?? '';

  const data: ChartEntry[] = main.map(cat => ({
    name: cat.category_name,
    value: cat.total_amount,
    category_name: cat.category_name,
    total_amount: cat.total_amount,
    transaction_count: cat.transaction_count,
    percentage_of_total: cat.percentage_of_total,
    currency: cat.currency,
  }));

  const colors = main.map((_, i) => COLORS[i % COLORS.length]);

  if (others.length > 0) {
    data.push({
      name: 'Others',
      value: others.reduce((sum, cat) => sum + cat.total_amount, 0),
      category_name: 'Others',
      total_amount: others.reduce((sum, cat) => sum + cat.total_amount, 0),
      transaction_count: others.reduce((sum, cat) => sum + cat.transaction_count, 0),
      percentage_of_total: others.reduce((sum, cat) => sum + cat.percentage_of_total, 0),
      currency,
    });
    colors.push(OTHERS_COLOR);
  }

  return { data, colors, othersDetail: others };
};

export const CategoryBreakdownCharts = ({ data, loading, rangeLabel }: CategoryBreakdownChartsProps) => {
  const isMobile = useIsMobile();

  if (loading) {
    return (
      <Card className="shadow-sm mt-6">
        <CardHeader>
          <CardTitle className="text-base">Category Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-64">
            <p className="text-muted-foreground">Loading category data...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card className="shadow-sm mt-6">
        <CardHeader>
          <CardTitle className="text-base">Category Breakdown - {rangeLabel}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-32">
            <p className="text-sm text-muted-foreground">No expense data available for this period</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Determine grid columns based on number of months
  const gridCols = data.length <= 2
    ? `grid-cols-1 md:grid-cols-${data.length}`
    : data.length <= 4
      ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-' + data.length
      : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';

  return (
    <Card className="shadow-sm mt-6">
      <CardHeader>
        <CardTitle className="text-base">Category Breakdown - {rangeLabel}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className={`grid ${gridCols} gap-6`}>
          {data.map((monthData) => {
            const { data: chartData, colors, othersDetail } = buildChartData(monthData.categories);

            return (
              <div key={monthData.month} className="flex flex-col">
                <div className="mb-2">
                  <h3 className="font-semibold text-center">{formatMonth(monthData.month)}</h3>
                  <p className="text-sm text-muted-foreground text-center">
                    Total Outflow: {formatCurrency(monthData.total_outflow)}
                  </p>
                </div>

                {chartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={isMobile ? 200 : 250}>
                    <PieChart>
                      <Pie
                        data={chartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ percentage_of_total }) => {
                          const rounded = Math.round(percentage_of_total);
                          const displayPercentage = rounded === 0 && percentage_of_total > 0 ? 1 : rounded;
                          return `${displayPercentage}%`;
                        }}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {chartData.map((_entry, index) => (
                          <Cell key={`cell-${index}`} fill={colors[index]} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[250px]">
                    <p className="text-sm text-muted-foreground">No data available</p>
                  </div>
                )}

                {/* Legend for categories */}
                <TooltipProvider>
                  <div className="mt-2 space-y-1">
                    {chartData.map((cat, idx) => {
                      const isOthers = cat.category_name === 'Others';
                      return (
                        <div key={idx} className="flex items-center gap-2 text-xs">
                          <div
                            className="w-3 h-3 rounded-sm flex-shrink-0"
                            style={{ backgroundColor: colors[idx] }}
                          />
                          <span className="truncate flex-1" title={cat.category_name}>{cat.category_name}</span>
                          {isOthers && othersDetail.length > 0 && (
                            <RadixTooltip>
                              <TooltipTrigger asChild>
                                <Info className="w-3 h-3 text-muted-foreground/60 flex-shrink-0 cursor-pointer hover:text-muted-foreground" />
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-w-[240px] text-xs p-3">
                                <p className="font-semibold mb-1.5">Others breakdown</p>
                                <div className="space-y-1">
                                  {othersDetail.slice(0, 10).map((o, i) => (
                                    <div key={i} className="flex items-center justify-between gap-3">
                                      <span className="truncate">{o.category_name}</span>
                                      <span className="text-muted-foreground shrink-0">{o.percentage_of_total.toFixed(1)}%</span>
                                    </div>
                                  ))}
                                  {othersDetail.length > 10 && (
                                    <p className="text-muted-foreground/70 pt-1">+{othersDetail.length - 10} more</p>
                                  )}
                                </div>
                              </TooltipContent>
                            </RadixTooltip>
                          )}
                          <span className="text-muted-foreground whitespace-nowrap">
                            {formatCurrency(cat.total_amount)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </TooltipProvider>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
